import type { FilePublic } from '@repo/types/files';
import type { MessagePublic, MessageReadReceipts, MessageSend } from '@repo/types/message';
import type { UserPublic } from '@repo/types/users';

import { create } from 'zustand';

import type { ChatStoreAction, ChatStoreState } from '.';

const defaultState: ChatStoreState = {
  chatId: null,
  connectionStatus: 'disconnected',
  isDisconnected: false,
  messages: [],
  last: undefined,
  newest: undefined,
  hasMore: true,
  loading: false,
  replyingTo: null,
  viewedFile: null,
  openViewer: false,
  messageConfirmationTimeouts: new Map(),
};

export const useChatStore = create<ChatStoreState & ChatStoreAction>((set, get) => ({
  ...defaultState,
  setActiveChat: (chatId: number) => set({ chatId }),
  setLoading: (loading: boolean) => set({ loading }),
  setReplyingTo: (replyingTo: MessagePublic | null) => set({ replyingTo }),
  setIsDisconnected: (isDisconnected: boolean) => set({ isDisconnected }),
  setLast: (last: number) => set({ last }),
  setNewest: (newest: number) => set({ newest }),
  setHasMore: (hasMore: boolean) => set({ hasMore }),
  setViewedFile: (viewedFile: FilePublic | File | null) => set({ viewedFile }),
  setOpenViewer: (openViewer: boolean) => set({ openViewer }),
  setConnectionStatus: (status) => {
    set({ connectionStatus: status });
  },
  deleteMessage: (id, content) => {
    set(({ messages }) => {
      messages.forEach((message) => {
        if (message.id === id) {
          message.content = content;
          message.attachment = null;
          message.type = 'deleted';
        }
      });
      return { messages: [...messages] };
    });
  },
  appendMessages: (newMessages: MessageSend[]) =>
    set(({ messages }) => {
      const sortedNewMessages = [...newMessages].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      return {
        messages: [...messages, ...sortedNewMessages],
      };
    }),
  prependMessages: (newMessages: MessagePublic[]) =>
    set(({ messages }) => {
      const sortedNewMessages = [...newMessages].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      return {
        messages: [...sortedNewMessages, ...messages],
      };
    }),
  confirmMessage: ({ action, id, tempUUID, content, readReceipts }) => {
    set(({ messages }) => {
      const newMessages = [...messages].map((message) => {
        if (action === 'sent' && message.tempUUID === tempUUID && id) {
          return {
            ...message,
            id,
            readReceipts: readReceipts,
            tempUUID: undefined,
            status: undefined,
          };
        } else if (action === 'deleted' && message.id === id) {
          return { ...message, type: 'deleted', content: content as string };
        } else if (action === 'failed' && message.tempUUID === tempUUID) {
          return { ...message, type: 'error', status: 'error' as const };
        }
        return { ...message };
      });
      return { messages: newMessages };
    });

    if (tempUUID) {
      get().clearMessageConfirmationTimeout(tempUUID);
    }
  },

  updateMessageStatus: (tempUUID, status) => {
    set(({ messages }) => {
      const newMessages = [...messages].map((message) => {
        if (message.tempUUID === tempUUID) {
          return {
            ...message,
            status,
          };
        }
        return message;
      });
      return { messages: newMessages };
    });
  },

  setMessageConfirmationTimeout: (tempUUID: string) => {
    const { messageConfirmationTimeouts } = get();

    if (messageConfirmationTimeouts.has(tempUUID)) {
      clearTimeout(messageConfirmationTimeouts.get(tempUUID));
    }

    const timeoutId = window.setTimeout(() => {
      get().confirmMessage({
        action: 'failed',
        tempUUID: tempUUID,
        content: 'Nie udało się wysłać wiadomości',
        type: 'text',
        id: null,
        readReceipts: null,
      });
      get().clearMessageConfirmationTimeout(tempUUID);
    }, 20000);

    set((state) => ({
      messageConfirmationTimeouts: new Map(state.messageConfirmationTimeouts).set(tempUUID, timeoutId),
    }));
  },

  clearMessageConfirmationTimeout: (tempUUID: string) => {
    const { messageConfirmationTimeouts } = get();

    if (messageConfirmationTimeouts.has(tempUUID)) {
      clearTimeout(messageConfirmationTimeouts.get(tempUUID));

      set((state) => {
        const newTimeouts = new Map(state.messageConfirmationTimeouts);
        newTimeouts.delete(tempUUID);
        return { messageConfirmationTimeouts: newTimeouts };
      });
    }
  },

  resetToDefaults: () =>
    set({
      ...defaultState,
    }),
  addReadReceipt: (upToMessageId: number, user: UserPublic, readAt?: string) => {
    set(({ messages }) => {
      // Reading a message implies reading everything before it — the server
      // records receipts the same way. Pending messages (id <= 0) aren't stored yet.
      const isUnreadByUser = (msg: MessageSend): boolean => msg.id > 0 && msg.id <= upToMessageId && !msg.readReceipts?.some((r) => r.id === user.id);
      if (!messages.some(isUnreadByUser)) {
        return {};
      }

      // Replace the message and its receipts array rather than pushing in place:
      // consumers memoize on `message.readReceipts`, so an in-place mutation
      // leaves the "read by" dialog showing a stale (often empty) list.
      const receipt: MessageReadReceipts = {
        ...user,
        messagereadreceipts: {
          createdAt: readAt ?? new Date().toISOString(),
        },
      };
      return {
        messages: messages.map((msg) => (isUnreadByUser(msg) ? { ...msg, readReceipts: [...(msg.readReceipts ?? []), receipt] } : msg)),
      };
    });
  },
}));
